import { useEffect, useRef, useState } from 'react';
import { onSnapshot, type DocumentData, type DocumentReference, type Query, type QueryDocumentSnapshot, type FirestoreError } from 'firebase/firestore';
import type { AsyncData } from './firestoreHooks';

/** Query one authorized class at a time, without reading hidden class content. */
export function useCourseSubscriptions<T>(build: () => Array<DocumentReference<DocumentData> | Query<DocumentData>>,
  map: (snapshot: QueryDocumentSnapshot<DocumentData>) => T, key: string): AsyncData<T[]> {
  const buildRef=useRef(build), mapRef=useRef(map);
  buildRef.current=build; mapRef.current=map;
  const [state,setState]=useState<AsyncData<T[]> & {key:string}>({key:'',data:[],loading:true,error:null,stale:false});
  useEffect(() => {
    const sources=buildRef.current();
    if (!sources.length) {setState({key,data:[],loading:false,error:null,stale:false}); return;}
    const rows=new Map<number,T[]>(), loaded=new Set<number>(), stale=new Set<number>();
    const failures=new Map<number,FirestoreError>();
    let live=true;
    const emit=() => {if (live) setState({key,data:[...rows.values()].flat(),loading:loaded.size<sources.length,error:failures.values().next().value??null,stale:stale.size>0});};
    emit();
    const fail=(index:number,error:FirestoreError) => {rows.set(index,[]);loaded.add(index);failures.set(index,error);emit();};
    const subscriptions=sources.map((source,index) => {
      if (source.type==='document') return onSnapshot(source,{includeMetadataChanges:true},snapshot => {
        rows.set(index,snapshot.exists()?[mapRef.current(snapshot)]:[]);loaded.add(index);failures.delete(index);
        if(snapshot.metadata.fromCache)stale.add(index);else stale.delete(index);emit();
      },error=>fail(index,error));
      return onSnapshot(source,{includeMetadataChanges:true},snapshot => {
        rows.set(index,snapshot.docs.map(mapRef.current));loaded.add(index);failures.delete(index);
        if(snapshot.metadata.fromCache)stale.add(index);else stale.delete(index);emit();
      },error=>fail(index,error));
    });
    return () => {live=false;subscriptions.forEach(stop=>stop());};
  },[key]);
  return state.key===key?state:{data:[],loading:true,error:null,stale:false};
}
